import React, { useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Link, OutlinedIcons } from '@marageti/z4-lib';
import { LoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { ReactComponent as ChevronIcon } from '../../../../assets/svg/chevron.svg';
import FieldError, { errorOutlinedInputSx } from '../../field-error';
import { linkSx, solidButtonSx } from '../../styles';

export interface PasswordVerificationViewProps {
  email: string;
  isSubmitting?: boolean;
  passwordError?: boolean;
  onSubmit: (request: LoginRequest) => void | Promise<void>;
  onClearError: () => void;
  onChangeEmail: () => void;
}

const buildResetSendEmailPath = (email: string): string => {
  const params = new URLSearchParams(window.location.search);
  params.set('email', email);
  return `/reset/send-email?${params.toString()}`;
};

const PasswordVerificationView = ({
  email,
  isSubmitting = false,
  passwordError = false,
  onSubmit,
  onClearError,
  onChangeEmail,
}: PasswordVerificationViewProps): React.ReactElement => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const canSubmit = password.length > 0 && !isSubmitting;

  const handlePasswordChange = (value: string) => {
    if (passwordError) {
      onClearError();
    }
    setPassword(value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSubmit({ username: email, password });
  };

  return (
    <Box
      width="100%"
      component="form"
      onSubmit={handleSubmit}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2} width="100%">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            aria-label="Change email"
            onClick={onChangeEmail}
            disabled={isSubmitting}
            sx={{
              color: '#070707',
              ml: -1,
              alignSelf: 'flex-start',
            }}
          >
            <ChevronIcon style={{ transform: 'rotate(180deg)', width: 20, height: 20 }} />
          </IconButton>
        </Box>

        <Stack spacing={2} alignItems="center" width="100%">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Satoshi, sans-serif',
              fontWeight: 500,
              fontSize: '28px',
              lineHeight: '40px',
              letterSpacing: 0,
              textAlign: 'center',
              color: '#070707',
            }}
          >
            Enter Your Password
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Signing in as
            {' '}
            <strong>{email}</strong>
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2} width="100%" sx={{ mt: '40px' }}>
        {/* Hidden username for password managers */}
        <TextField
          name="username"
          autoComplete="username"
          value={email}
          sx={{ display: 'none' }}
          inputProps={{ readOnly: true }}
        />
        <TextField
          id="login-password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          fullWidth
          value={password}
          disabled={isSubmitting}
          error={passwordError}
          onChange={(e) => handlePasswordChange(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  disabled={isSubmitting}
                >
                  {showPassword
                    ? <OutlinedIcons.OutlinedEyeOffIcon color="primary" />
                    : <OutlinedIcons.OutlinedEyeIcon color="primary" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              ...(passwordError && errorOutlinedInputSx),
            },
          }}
        />

        {passwordError && (
          <FieldError message="Password doesn't match. Try again, or reset your password below." />
        )}

        <Link
          to={buildResetSendEmailPath(email)}
          underline="always"
          sx={{ ...linkSx, alignSelf: 'flex-start' }}
        >
          Forgot Password?
        </Link>
      </Stack>

      <Box sx={{ mt: 'auto', pt: 4 }}>
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={isSubmitting}
          disabled={!canSubmit}
          sx={solidButtonSx}
        >
          Sign In
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default PasswordVerificationView;
